import { useState, useCallback, useEffect } from 'react';
import { validateFormStep, validateField, createDebouncedValidator } from '../utils/wizardValidation.js';

/**
 * Custom hook for form validation with real-time feedback
 */
export const useFormValidation = (initialValues = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);

  // Debounced validation function
  const debouncedValidate = useCallback(
    createDebouncedValidator((fieldName, value) => {
      const error = validateField(fieldName, value, validationRules[fieldName]);
      setErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
    }, 300),
    [validationRules]
  );

  // Validate single field
  const validateSingleField = useCallback((fieldName, value) => {
    const error = validateField(fieldName, value, validationRules[fieldName]);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    return !error;
  }, [validationRules]);

  // Validate all fields
  const validateAllFields = useCallback(() => {
    const allErrors = validateForm(values, validationRules);
    setErrors(allErrors);
    const valid = Object.keys(allErrors).length === 0;
    setIsValid(valid);
    return valid;
  }, [values, validationRules]);

  // Handle field change
  const handleChange = useCallback((fieldName, value) => {
    setValues(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error immediately when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }

    // Debounced validation for real-time feedback
    if (touched[fieldName]) {
      debouncedValidate(fieldName, value);
    }
  }, [errors, touched, debouncedValidate]);

  // Handle field blur
  const handleBlur = useCallback((fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    // Validate immediately on blur
    validateSingleField(fieldName, values[fieldName]);
  }, [values, validateSingleField]);

  // Handle form submission
  const handleSubmit = useCallback((onSubmit) => {
    return (e) => {
      e.preventDefault();
      
      // Mark all fields as touched
      const allTouched = {};
      Object.keys(validationRules).forEach(field => {
        allTouched[field] = true;
      });
      setTouched(allTouched);

      // Validate all fields
      const valid = validateAllFields();
      
      if (valid && onSubmit) {
        onSubmit(values);
      }
    };
  }, [values, validationRules, validateAllFields]);

  // Reset form
  const reset = useCallback((newValues = initialValues) => {
    setValues(newValues);
    setErrors({});
    setTouched({});
    setIsValid(false);
  }, [initialValues]);

  // Set field value programmatically
  const setValue = useCallback((fieldName, value) => {
    setValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  // Set multiple values
  const setMultipleValues = useCallback((newValues) => {
    setValues(prev => ({
      ...prev,
      ...newValues
    }));
  }, []);

  // Set field error
  const setFieldError = useCallback((fieldName, error) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  }, []);

  // Clear field error
  const clearFieldError = useCallback((fieldName) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: null
    }));
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Update isValid when errors change
  useEffect(() => {
    const hasErrors = Object.values(errors).some(error => error !== null && error !== '');
    setIsValid(!hasErrors && Object.keys(values).length > 0);
  }, [errors, values]);

  // Get field props for easy integration with form inputs
  const getFieldProps = useCallback((fieldName) => ({
    value: values[fieldName] || '',
    onChange: (e) => handleChange(fieldName, e.target.value),
    onBlur: () => handleBlur(fieldName),
    error: touched[fieldName] ? errors[fieldName] : null,
    hasError: touched[fieldName] && !!errors[fieldName]
  }), [values, errors, touched, handleChange, handleBlur]);

  // Get checkbox field props
  const getCheckboxProps = useCallback((fieldName) => ({
    checked: !!values[fieldName],
    onChange: (e) => handleChange(fieldName, e.target.checked),
    onBlur: () => handleBlur(fieldName),
    error: touched[fieldName] ? errors[fieldName] : null,
    hasError: touched[fieldName] && !!errors[fieldName]
  }), [values, errors, touched, handleChange, handleBlur]);

  // Get select field props
  const getSelectProps = useCallback((fieldName) => ({
    value: values[fieldName] || '',
    onChange: (e) => handleChange(fieldName, e.target.value),
    onBlur: () => handleBlur(fieldName),
    error: touched[fieldName] ? errors[fieldName] : null,
    hasError: touched[fieldName] && !!errors[fieldName]
  }), [values, errors, touched, handleChange, handleBlur]);

  return {
    values,
    errors,
    touched,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    validateAllFields,
    validateSingleField,
    reset,
    setValue,
    setValues: setMultipleValues,
    setFieldError,
    clearFieldError,
    clearErrors,
    getFieldProps,
    getCheckboxProps,
    getSelectProps
  };
};

export default useFormValidation;
